import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Anchor, Group, List, Paper, SimpleGrid, Stack, Text } from '@mantine/core'
import { SectionCard } from '@lighthouse/design'
import { AuditDetailModal } from '../audit/AuditDetailModal'
import { AuditTrailTable } from '../audit/AuditTrailTable'
import { useReportHistory } from '../../hooks/useReportDetail'
import { errorKind } from '../../utils/format'
import type { ReportDetailOverview } from '../../types/reportDetail'
import { Caption, TabFailure } from './OverviewTab'
import {
  ACCESS_REQUESTS_NOT_HERE,
  AUDITED_FOR_REPORT,
  AUDIT_NEVER_TRIMMED,
  NOT_AUDITED,
  TRAILS_NOT_MERGED,
  auditLogPath,
  membershipLinkLabel,
} from './reportDetailFormat'

/**
 * Report Detail → History: the audit trail for this report, through the Audit
 * Log's existing `entityId` filter, in the Audit Log's own table
 * (components/audit/AuditTrailTable.tsx) and detail modal.
 *
 * TWO TRAILS, NOT MERGED. Grants, transfers, filing, archive and retirement are
 * recorded against the report's id; workspace membership changes against the
 * WORKSPACE's id, because one membership change reaches every report filed
 * there. This tab shows the first and links to both; merging them here would
 * need a second query and an interleave that the Audit Log itself does not do.
 *
 * WHAT IS NOT HERE, said rather than implied by absence: definition edits,
 * renames, schedule changes, favourites and runs are audited nowhere, and
 * access-request rows are keyed by the request's id.
 *
 * `overview` is the page's Overview read, for the workspace link; while it is
 * loading the workspace link is not drawn.
 */
export function HistoryTab({ reportId, overview }: { reportId: string; overview?: ReportDetailOverview | null }) {
  const [page, setPage] = useState(1)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const { data, isLoading, error } = useReportHistory(reportId, page, { open: true })

  const openDetail = (id: string) => {
    setDetailId(id)
    setModalOpen(true)
  }

  return (
    <Stack gap="lg">
      <Group gap="lg" wrap="wrap">
        <Anchor component={Link} to={auditLogPath(reportId)} size="sm">Open in Audit Log →</Anchor>
        <WorkspaceTrailLink overview={overview ?? null} />
      </Group>
      <Caption>{TRAILS_NOT_MERGED} {AUDIT_NEVER_TRIMMED}</Caption>

      {errorKind(error) === 'forbidden' ? (
        <TabFailure error={error} what="this report’s audit trail" />
      ) : (
        <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <AuditTrailTable
            data={data}
            isLoading={isLoading}
            error={error}
            page={page}
            onPageChange={setPage}
            onOpenDetail={openDetail}
            emptyMessage="No audit entry is recorded against this report."
          />
        </Paper>
      )}

      <SectionCard title="What the trail records for a report">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <Stack gap={4} data-list="audited">
            <Text size="sm" fw={500}>Audited, against the report</Text>
            <List size="sm" spacing={2}>
              {AUDITED_FOR_REPORT.map((line) => <List.Item key={line}>{line}</List.Item>)}
            </List>
          </Stack>
          <Stack gap={4} data-list="not-audited">
            <Text size="sm" fw={500}>Not audited anywhere</Text>
            <List size="sm" spacing={2}>
              {NOT_AUDITED.map((line) => <List.Item key={line}>{line}</List.Item>)}
            </List>
          </Stack>
        </SimpleGrid>
        <Caption>{ACCESS_REQUESTS_NOT_HERE}</Caption>
      </SectionCard>

      <AuditDetailModal logId={detailId} opened={modalOpen} onClose={() => setModalOpen(false)} />
    </Stack>
  )
}

function WorkspaceTrailLink({ overview }: { overview: ReportDetailOverview | null }) {
  if (overview == null) return null
  if (overview.isUnfiled || overview.workspaceId == null) {
    return <Text size="xs" c="dimmed">Not filed in a workspace, so no membership trail reaches it.</Text>
  }
  if (overview.isPersonalWorkspace) {
    return <Text size="xs" c="dimmed">Filed in a personal workspace, which has no members to change.</Text>
  }
  return (
    <Anchor component={Link} to={auditLogPath(overview.workspaceId)} size="sm">
      {membershipLinkLabel(overview.workspaceName)} →
    </Anchor>
  )
}
